import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

export interface TabItem {
  id: string;
  label: ReactNode;
  content: ReactNode;
}

export interface TabsProps {
  label: string;
  items: TabItem[];
  value?: string;
  onChange?: (id: string) => void;
  className?: string;
}

/** WAI-ARIA tabs with automatic activation (arrow keys, Home, End). */
export function Tabs({ label, items, value, onChange, className }: TabsProps) {
  const base = useId();
  const [internal, setInternal] = useState(items[0]?.id);
  const active = value ?? internal;
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  if (!items.length) throw new Error('Tabs needs at least one item');

  const select = (i: number) => {
    const item = items[(i + items.length) % items.length]!;
    setInternal(item.id);
    onChange?.(item.id);
    refs.current[(i + items.length) % items.length]?.focus();
  };

  const onKey = (e: KeyboardEvent, i: number) => {
    const map: Record<string, number> = {
      ArrowRight: i + 1,
      ArrowLeft: i - 1,
      Home: 0,
      End: items.length - 1,
    };
    const next = map[e.key];
    if (next === undefined) return;
    e.preventDefault();
    select(next);
  };

  const current = items.find((t) => t.id === active) ?? items[0]!;
  return (
    <div className={className}>
      <div role="tablist" aria-label={label} className="fl-tabs-list">
        {items.map((t, i) => {
          const selected = t.id === current.id;
          return (
            <button
              key={t.id}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`${base}-tab-${t.id}`}
              aria-selected={selected}
              aria-controls={`${base}-panel-${t.id}`}
              tabIndex={selected ? 0 : -1}
              className="fl-tab"
              onClick={() => select(i)}
              onKeyDown={(e) => onKey(e, i)}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <div
        role="tabpanel"
        id={`${base}-panel-${current.id}`}
        aria-labelledby={`${base}-tab-${current.id}`}
        tabIndex={0}
        className="pt-6 focus-visible:outline-offset-4"
      >
        {current.content}
      </div>
    </div>
  );
}
